// The speech WebSocket, client side (C-4, C-7 `class SpeechSocket`).
//
// A thin, policy-free wrapper: binary frames go up with the 12-byte header,
// JSON control messages go both ways. Unknown server message types are
// ignored (addendum A-5), so a newer server can add messages safely.
//
// `sendFrame` never drops audio: it returns false while the socket is not
// open or `bufferedAmount` is above CLIENT_BACKPRESSURE_BYTES, and the caller
// keeps the frame (backlog or spool) and tries again later.
import type { PcmFrame, SpeechSocketApi, SpeechSocketOptions } from '../contracts';
import {
  CLIENT_BACKPRESSURE_BYTES,
  CLOSE_CODES,
  encodeControl,
  encodeFrame,
  parseServerMessage,
  type ClientControl,
} from './protocol';

type SocketLike = {
  binaryType: string;
  readonly readyState: number;
  readonly bufferedAmount: number;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onopen: ((event: unknown) => void) | null;
  send(data: string | ArrayBufferView | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
};
export type WebSocketFactory = (url: string) => SocketLike;

const OPEN = 1;
/** The close code the client reports when the socket never delivered a close frame. */
export const ABNORMAL_CLOSURE = 1006;

const defaultFactory: WebSocketFactory = (url) => new WebSocket(url) as unknown as SocketLike;

export class SpeechSocket implements SpeechSocketApi {
  private readonly socket: SocketLike;
  private closed = false;

  constructor(private readonly options: SpeechSocketOptions & { createSocket?: WebSocketFactory }) {
    this.socket = (options.createSocket ?? defaultFactory)(options.url);
    this.socket.binaryType = 'arraybuffer';
    this.socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      const message = parseServerMessage(event.data);
      if (message) this.options.onMessage(message);
    };
    this.socket.onclose = (event) =>
      this.finish(event.code || ABNORMAL_CLOSURE, event.reason ?? '');
    // An error is always followed by a close; the close carries the outcome.
    this.socket.onerror = () => {};
  }

  get isOpen(): boolean {
    return !this.closed && this.socket.readyState === OPEN;
  }

  get bufferedAmount(): number {
    return this.socket.bufferedAmount;
  }

  sendFrame(frame: PcmFrame, flags = 0): boolean {
    if (!this.isOpen || this.socket.bufferedAmount > CLIENT_BACKPRESSURE_BYTES) return false;
    this.socket.send(
      encodeFrame({
        seq: frame.seq,
        sampleOffset: frame.sampleOffset,
        flags,
        pcm: new Uint8Array(frame.pcm, 0, frame.samples * 2),
      }),
    );
    return true;
  }

  send(control: ClientControl): void {
    if (this.isOpen) this.socket.send(encodeControl(control));
  }

  close(code: number = CLOSE_CODES.stopped): void {
    if (this.closed) return;
    try {
      this.socket.close(code);
    } catch {
      /* already closing */
    }
    // Report our own close at once; the browser's close event is ignored after this.
    this.finish(code, 'client');
  }

  private finish(code: number, reason: string) {
    if (this.closed) return;
    this.closed = true;
    this.socket.onmessage = null;
    this.options.onClose(code, reason);
  }
}
